import { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, money, fileSrc, useUrlSearch, SearchInput, matchText } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LayoutGrid, Rows3, Upload, ImageIcon } from "lucide-react";

const empty = {
  name: "", description: "", rate_per_hour: "", base_price: "",
  duration_hours: "", discount_percent: "", image_path: null, active: true,
};

export default function Packages() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("grid");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [search, setSearch] = useUrlSearch();

  const filtered = items.filter((p) => matchText(search, p.name, p.description));

  const load = () => api.get("/packages").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name, description: p.description, rate_per_hour: p.rate_per_hour,
      base_price: p.base_price, duration_hours: p.duration_hours,
      discount_percent: p.discount_percent, image_path: p.image_path || null, active: p.active,
    });
    setEditing(p.id); setOpen(true);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: data.path }));
      toast.success("Image attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Package name is required");
    const payload = {
      name: form.name, description: form.description,
      rate_per_hour: Number(form.rate_per_hour) || 0,
      base_price: Number(form.base_price) || 0,
      duration_hours: Number(form.duration_hours) || 0,
      discount_percent: Number(form.discount_percent) || 0,
      image_path: form.image_path,
      active: form.active,
    };
    try {
      if (editing) await api.put(`/packages/${editing}`, payload);
      else await api.post("/packages", payload);
      toast.success(editing ? "Package updated" : "Package created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    await api.delete(`/packages/${id}`);
    toast.success("Package deleted");
    load();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const priceAfter = (p) =>
    p.discount_percent > 0 ? p.base_price * (1 - p.discount_percent / 100) : p.base_price;

  return (
    <div>
      <PageHeader
        eyebrow="Service Offerings"
        title="Cleaning Packages"
        description="Structured, tailored packages — each with its own rate, duration and discount."
        action={
          isAdmin && (
            <Button onClick={openNew} data-testid="add-package-button" className="rounded-sm gap-2">
              <Plus className="w-4 h-4" /> New Package
            </Button>
          )
        }
      />

      {items.length > 0 && (
        <div className="flex justify-between items-center mb-6 gap-4">
          <SearchInput value={search} onChange={setSearch} placeholder="Search packages…" testid="packages-search" />
          <div className="flex gap-1 border border-border/60 rounded-sm p-1">
            <button onClick={() => setView("grid")} data-testid="package-view-grid" className={`px-3 py-2 rounded-sm ${view === "grid" ? "bg-[hsl(330_85%_93%)] grad-ink" : "text-muted-foreground hover:bg-accent/60"}`}>
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button onClick={() => setView("row")} data-testid="package-view-row" className={`px-3 py-2 rounded-sm ${view === "row" ? "bg-[hsl(330_85%_93%)] grad-ink" : "text-muted-foreground hover:bg-accent/60"}`}>
              <Rows3 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState
          title="No packages yet"
          description="Create packages like Express Reset or Deep Clean with rates and discounts."
          action={isAdmin && <Button onClick={openNew} className="rounded-sm">Add Package</Button>}
        />
      ) : filtered.length === 0 ? (
        <EmptyState title="No matches" description={`No packages match “${search}”.`} />
      ) : view === "row" ? (
        <div className="divide-y divide-border/60 border-y border-border/60">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`package-row-${p.id}`} className="flex items-center gap-6 py-5 group">
              <div className="w-20 h-16 shrink-0 bg-accent/50 rounded-sm overflow-hidden flex items-center justify-center">
                {p.image_path ? (
                  <img src={fileSrc(p.image_path)} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-xl"><span className="text-brand-gradient" data-testid={`package-list-name-${p.id}`}>{p.name}</span>{!p.active && <span className="label-caps text-muted-foreground ml-2">Inactive</span>}</div>
                <div className="text-sm text-muted-foreground truncate">{p.description || "—"}</div>
              </div>
              <div className="text-sm text-muted-foreground w-32 shrink-0">{money(p.rate_per_hour)}/hr · {p.duration_hours || 0}h</div>
              <div className="w-40 shrink-0 text-right">
                <span className="font-serif text-2xl">{money(priceAfter(p))}</span>
                {p.discount_percent > 0 && <div className="label-caps text-[hsl(140_25%_35%)]">{p.discount_percent}% off</div>}
              </div>
              {isAdmin && (
                <div className="w-20 shrink-0 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(p)} data-testid={`edit-package-${p.id}`} className="p-1.5 text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(p.id)} data-testid={`delete-package-${p.id}`} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <div
              key={p.id}
              data-testid={`package-card-${p.id}`}
              className="border border-border/60 bg-card rounded-sm flex flex-col group overflow-hidden"
            >
              {p.image_path && (
                <img src={fileSrc(p.image_path)} alt={p.name} className="w-full h-40 object-cover" />
              )}
              <div className="p-4 sm:p-8 flex flex-col flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    {!p.active && <span className="label-caps text-muted-foreground">Inactive</span>}
                    <h3 className="font-serif text-2xl tracking-tight text-brand-gradient pb-0.5" data-testid={`package-card-name-${p.id}`}>{p.name}</h3>
                  </div>
                  {isAdmin && (
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEdit(p)} data-testid={`edit-package-${p.id}`} className="p-1.5 text-muted-foreground hover:text-primary">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(p.id)} data-testid={`delete-package-${p.id}`} className="p-1.5 text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-2 flex-1">{p.description || "—"}</p>
                <div className="mt-6 pt-6 border-t border-border/60 space-y-2">
                  <div className="flex items-baseline gap-3">
                    <span className="font-serif text-3xl">{money(priceAfter(p))}</span>
                    {p.discount_percent > 0 && (
                      <>
                        <span className="text-muted-foreground line-through text-sm">{money(p.base_price)}</span>
                        <span className="label-caps text-[hsl(140_25%_35%)]">{p.discount_percent}% off</span>
                      </>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {money(p.rate_per_hour)}/hr · {p.duration_hours || 0} hrs
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing ? "Edit Package" : "New Package"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Package name</Label>
              <Input value={form.name} onChange={set("name")} data-testid="package-name" placeholder="Express Reset" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Description</Label>
              <Textarea value={form.description} onChange={set("description")} data-testid="package-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Rate / hour</Label>
                <Input type="number" value={form.rate_per_hour} onChange={set("rate_per_hour")} data-testid="package-rate" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Base price</Label>
                <Input type="number" value={form.base_price} onChange={set("base_price")} data-testid="package-base-price" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Duration (hrs)</Label>
                <Input type="number" value={form.duration_hours} onChange={set("duration_hours")} data-testid="package-duration" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Discount %</Label>
                <Input type="number" value={form.discount_percent} onChange={set("discount_percent")} data-testid="package-discount" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Package image</Label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-20 bg-accent/50 rounded-sm overflow-hidden flex items-center justify-center shrink-0">
                  {form.image_path ? (
                    <img src={fileSrc(form.image_path)} alt="Package" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="package-image-input" />
                <div>
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm gap-2" data-testid="package-image-upload">
                    <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Attach image"}
                  </Button>
                  {form.image_path && (
                    <button type="button" onClick={() => setForm({ ...form, image_path: null })} className="block text-xs text-destructive mt-2">Remove</button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Label className="label-caps text-muted-foreground">Active</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="package-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="save-package-button" className="rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
